import InputLabelField from '@/components/form-control/InputLabelField'
import { useAuthContext } from '@/features/auth/context'
import { useConfigUI } from '@/features/config/context'
import { useAddSoil, useUpdateSoil } from '@/features/config/hooks/soils'
import useSoilById from '@/features/config/hooks/soils/useSoilById'
import useTranslation from '@/hooks/useTranslation'
import { useUnits } from '@/hooks/useUnits'
import { yupResolver } from '@hookform/resolvers/yup'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  CircularProgress,
  Container,
  Fade,
  Stack,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useRef } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'

interface SoilViewProps {
  id?: string
}

const schema = yup
  .object({
    description: yup.string().required('Soil Description is required'),
    // holdingCapacity: yup
    //   .number()
    //   .required('Holding Capacity is required')
    //   .typeError('Holding Capacity is a number')
    //   .min(0, 'Minimum 0%')
    //   .max(100, 'Maximum 100%'),
    // wiltingPoint: yup
    //   .number()
    //   .required('Wilting Point is required')
    //   .typeError('Wilting Point is a number')
    //   .min(0, 'Minimum 0%')
    //   .max(100, 'Maximum 100%')
    //   .test(
    //     'smaller',
    //     'The wilting point needs to be less than holding capacity',
    //     (value, context) =>
    //       value == null || value < context.parent.holdingCapacity,
    //   ),
    // intakeRate: yup
    //   .number()
    //   .required('Intake Rate is required')
    //   .typeError('Intake Rate must be a number')
    //   .test(
    //     'is-number',
    //     'Intake Rate must be a number',
    //     (value) => value !== undefined && !isNaN(value),
    //   ),
    // maxSurfaceAccumulation: yup
    //   .number()
    //   .required('Surface is required')
    //   .typeError('Surface must be a number')
    //   .test(
    //     'is-number',
    //     'Surface must be a number',
    //     (value) => value !== undefined && !isNaN(value),
    //   ),
  })
  .required()

const SoilView = ({ id }: SoilViewProps) => {
  const { t } = useTranslation()
  const { closeSidebar } = useConfigUI()
  const { interfaceId } = useAuthContext()
  const {
    convertLabelUnit,
    convertToMetric,
    convertFromMetricV2,
    convertToMetricV2,
  } = useUnits()

  const inputRefSiteName = useRef<HTMLInputElement>(null)

  const { mutate: addSoil, isLoading: loadingAddSoil } = useAddSoil()

  const { mutate: editSoil, isLoading: loadingEditSoil } = useUpdateSoil()

  const { data: editItem, isLoading: isLoadingSoil } = useSoilById(
    interfaceId,
    id,
  )

  const mode = id ? 'edit' : 'create'
  const isLoading = loadingAddSoil || loadingEditSoil

  const defaultIntakeMetric = useMemo(
    () => convertToMetricV2(5, 'mm/h', 'in/h', 2) || 5,
    [convertToMetricV2],
  )

  const defaultMaxSurfMetric = useMemo(
    () => convertToMetricV2(5, 'mm', 'in', 2) || 5,
    [convertToMetricV2],
  )

  const defaultFormValues = useMemo(
    () => ({
      description: '',
      holdingCapacity: 50,
      wiltingPoint: 30,
      intakeRate: 5,
      maxSurfaceAccumulation: 5,
    }),
    [],
  )

  const form = useForm({
    defaultValues: {
      ...defaultFormValues,
      ...editItem,
    },
    resolver: yupResolver(schema),
  })

  const handleSave = (data: any) => {
    if (mode === 'create') {
      addSoil({
        interfaceId: Number(interfaceId),
        description: data.description,
        holdingCapacity: 50,
        wiltingPoint: 30,
        intakeRate: defaultIntakeMetric,
        maxSurfaceAccumulation: defaultMaxSurfMetric,
      })
    }
    if (mode === 'edit') {
      editSoil({
        id: Number(id),
        interfaceId: Number(interfaceId),
        description: data.description,
        holdingCapacity: editItem?.holdingCapacity ?? 50,
        wiltingPoint: editItem?.wiltingPoint ?? 30,
        intakeRate: editItem?.intakeRate ?? defaultIntakeMetric,
        maxSurfaceAccumulation:
          editItem?.maxSurfaceAccumulation ?? defaultMaxSurfMetric,
      })
    }
  }

  useEffect(() => {
    if (inputRefSiteName.current) {
      inputRefSiteName.current.focus()
    }
  }, [])

  useEffect(() => {
    if (editItem?.id) {
      form.reset({
        id: editItem.id,
        description: editItem.description,
      })
    } else {
      form.reset(defaultFormValues)
    }
  }, [editItem, defaultFormValues, form])

  return (
    <Container sx={{ maxWidth: '450px', height: '100vh' }}>
      {isLoadingSoil && (
        <Stack
          width="377px"
          height="100%"
          justifyContent="center"
          alignItems="center"
        >
          <CircularProgress />
        </Stack>
      )}
      {!isLoadingSoil && (
        <Fade in={!isLoadingSoil}>
          <form onSubmit={form.handleSubmit(handleSave)}>
            <Stack sx={{ padding: '34px 18px' }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: '377px' }}
              >
                <Typography
                  sx={{
                    fontWeight: '500',
                    lineHeight: '14px',
                    color: 'text.main',
                  }}
                >
                  {id ? t('dataConfigEditSoil') : t('dataConfigAddSoil')}
                </Typography>
                <Stack direction="row">
                  <Button
                    variant="secondary"
                    size="small"
                    sx={{ marginRight: '7px', width: '85px' }}
                    onClick={() => closeSidebar()}
                  >
                    {t('commonActionsCancel')}
                  </Button>

                  <LoadingButton
                    loading={isLoading}
                    size="small"
                    variant="primary"
                    sx={{ minWidth: '72px' }}
                    type="submit"
                  >
                    {t('commonActionsSave')}
                  </LoadingButton>
                </Stack>
              </Stack>

              <InputLabelField
                name="description"
                form={form}
                label={t('soilDescription')}
                placeholder={t('soilDescription')}
                style={{
                  height: 50,
                  borderRadius: '4px',
                  width: { xs: '377px' },
                  marginTop: '8px',
                }}
                padding="8px 16px"
              />
            </Stack>
          </form>
        </Fade>
      )}
    </Container>
  )
}

export default SoilView
